import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import React__default, { useState, useRef, useCallback, useEffect } from 'react';
import { toast, Toaster } from 'sonner';
import { useBrandAppConfig } from '../api/cache/appConfigQueryOptions.js';
import { ConciergeChatAreaV2 } from './components/ConciergeChatAreaV2.js';
import { useConciergeContext } from './ConciergeContextProvider.js';
import { ConciergeWidgetHeader } from './ConciergeWidgetHeader.js';
import { useAutoScroll } from './hooks/useAutoScroll.js';
import { useConversationLoader } from './hooks/useConversationLoader.js';
import { usePageContext } from './hooks/usePageContext.js';
import { useStreamingSearch } from './hooks/useStreamingSearch.js';
import { useConciergePendingQuery } from './stores/useConciergePendingQuery.js';
import { wrapSearchWithProductContext } from './utils/wrapSearchWithProductContext.js';

const ConciergeWidgetChatContent = ({ pageContext: providedPageContext, widgetState, showMaximize = true, isMaximized = false, onMaximize, onMinimize, title, }) => {
    // Get conversation state from context (includes apiBaseUrl for checkout-shop-app)
    const conversationState = useConciergeContext();
    // Brand config
    const { data: config } = useBrandAppConfig(conversationState.apiBaseUrl);
    const params = new URLSearchParams(window.location.search);
    const brandId = params.get('brandId') || null;
    const appConfigId = params.get('appConfigId') || null;
    // Input state
    const [searchInputValue, setSearchInputValue] = useState('');
    // Get page context - use provided one or fallback to hook
    // usePageContext will work even without react-router (uses window.location)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const hookPageContext = usePageContext();
    const pageContext = providedPageContext !== null && providedPageContext !== void 0 ? providedPageContext : hookPageContext;
    // Get widget state and pending query
    const { isOpen } = widgetState;
    const { pendingQuery, pendingStacklineSku, pendingQueryId, clearPendingQuery } = useConciergePendingQuery();
    // Ref to track the last processed query ID to prevent duplicate sends
    const lastProcessedQueryIdRef = useRef(null);
    // Auto-scroll behavior
    const { chatWrapperRef, messagesEndRef, shouldScrollOnNextMessageRef, scrollToBottom } = useAutoScroll(conversationState.conversationHistory.length, conversationState.isActivelyStreaming, conversationState.streamingAIResponse, conversationState.conversationHistory.length === 0);
    // Handle stream errors - restore input and show appropriate toast
    const handleStreamError = useCallback((errorType, originalMessage) => {
        setSearchInputValue(originalMessage);
        const errorMessages = {
            rate_limit: 'Too many requests. Please wait a moment before trying again.',
            network: 'Network error. Please check your connection and try again.',
            server: 'Something went wrong on our end. Please try again.',
            unknown: 'Something went wrong. Please try again.',
        };
        toast.error(errorMessages[errorType]);
    }, []);
    // Streaming search - expose performSearch for direct calls from pending query handler
    const { performSearch, cancelStream } = useStreamingSearch({
        brandId,
        appConfigId,
        conversationState,
        scrollToBottom,
        shouldScrollOnNextMessageRef,
        onStreamError: handleStreamError,
    });
    // Handle search submission
    const handleSearch = useCallback((searchTerm, imageData, imageMimeType) => __awaiter(void 0, void 0, void 0, function* () {
        setSearchInputValue('');
        // Wrap search term with product context if on a PDP
        const searchTermWithContext = wrapSearchWithProductContext(searchTerm, pageContext);
        yield performSearch(searchTermWithContext, imageData, imageMimeType);
    }), [pageContext, performSearch]);
    // Handle update feedback
    const handleUpdateFeedback = (messageIndex, feedback) => {
        // Update the feedback in the conversation history
        conversationState.setConversationHistory((prev) => {
            return prev.map((msg, idx) => {
                if (idx === messageIndex) {
                    return Object.assign(Object.assign({}, msg), { feedback: feedback || undefined });
                }
                return msg;
            });
        });
    };
    // Conversation loader
    const { isLoadingConversationQuery } = useConversationLoader({
        conversationId: conversationState.conversationId,
        isActivelyStreaming: conversationState.isActivelyStreaming,
        conversationHistoryLength: conversationState.conversationHistory.length,
        apiBaseUrl: conversationState.apiBaseUrl,
        onConversationLoaded: (messages, productMap, followUpQuestions, previewSearchQueries, previewQueryProducts) => {
            conversationState.setProductMetadataMap(productMap);
            conversationState.setConversationHistory(messages);
            conversationState.setFollowUpQuestions(followUpQuestions);
            conversationState.setPreviewSearchQueries(previewSearchQueries);
            conversationState.setPreviewQueryProducts(previewQueryProducts);
        },
        onConversationNotFound: () => {
            conversationState.setConversationId(null);
        },
    });
    // Sync conversationId with URL (disabled in widget)
    // useUrlSync(conversationState.conversationId);
    // Auto-send from URL params (disabled in widget)
    // useAutoSendFromUrl({
    //   isActivelyStreaming: conversationState.isActivelyStreaming,
    //   conversationLength: conversationState.conversationHistory.length,
    //   onSearch: handleSearch,
    // });
    // Handle pending queries from PDP conversation starters
    // This effect runs when: widget opens, or a new pending query is set
    useEffect(() => {
        // Skip if no pending query or widget not open
        if (!pendingQuery || !pendingQueryId || !isOpen) {
            return;
        }
        // Skip if we already processed this exact query (by ID)
        if (lastProcessedQueryIdRef.current === pendingQueryId) {
            return;
        }
        // Mark as processed immediately to prevent re-processing
        lastProcessedQueryIdRef.current = pendingQueryId;
        // Capture values before clearing
        const queryToSend = pendingQuery;
        const stacklineSkuToUse = pendingStacklineSku;
        // Clear the pending query immediately
        clearPendingQuery();
        // Wrap query with product context if we have a stacklineSku
        let messageToSend = queryToSend;
        if (stacklineSkuToUse) {
            const productPageContext = {
                pageType: 'pdp',
                stacklineSku: stacklineSkuToUse,
            };
            messageToSend = wrapSearchWithProductContext(queryToSend, productPageContext);
        }
        // Send the message - performSearch handles adding user message and streaming response
        performSearch(messageToSend);
    }, [isOpen, pendingQuery, pendingQueryId, pendingStacklineSku, performSearch, clearPendingQuery]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Toaster, null),
        React__default.createElement("div", { className: "concierge-root", style: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                maxHeight: '100%',
                overflow: 'hidden',
            } },
            React__default.createElement(ConciergeWidgetHeader, { showMaximize: showMaximize, isMaximized: isMaximized, onMaximize: onMaximize, onMinimize: onMinimize, onClose: widgetState.close, title: title }),
            React__default.createElement(ConciergeChatAreaV2, { appName: config === null || config === void 0 ? void 0 : config.appName, brandId: brandId, conversationId: conversationState.conversationId, isEmbedded: true, apiBaseUrl: conversationState.apiBaseUrl, conversationHistory: conversationState.conversationHistory, productMetadataMap: conversationState.productMetadataMap, streamingProductGroups: conversationState.streamingProductGroups, previewSearchQueries: conversationState.previewSearchQueries, previewQueryProducts: conversationState.previewQueryProducts, followUpQuestions: conversationState.followUpQuestions, streamingFeaturedProduct: conversationState.streamingFeaturedProduct, isActivelyStreaming: conversationState.isActivelyStreaming, streamingAIResponse: conversationState.streamingAIResponse, currentStatus: conversationState.currentStatus, isWaitingForFollowUpQueries: conversationState.isWaitingForFollowUpQueries, isLoadingConversationQuery: isLoadingConversationQuery, handleSearch: handleSearch, onUpdateFeedback: handleUpdateFeedback, onStop: cancelStream, searchInputValue: searchInputValue, setSearchInputValue: setSearchInputValue }))));
};
// ConciergeWidgetChat expects to be inside a QueryClientProvider (provided by ConciergeStandalone)
const ConciergeWidgetChat = (props) => {
    return React__default.createElement(ConciergeWidgetChatContent, Object.assign({}, props));
};

export { ConciergeWidgetChat };
//# sourceMappingURL=ConciergeWidgetChat.js.map
